/**
 * Seattle Metro Solo - Phase 5: 商品輸送バリデーター
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_05_move_goods_seattle.md
 */

import type { Ctx } from 'boardgame.io';
import type { SeattleGameState, MoveGoodsSeattleArgs } from '../../types';

/** Gray City の ID リスト */
const SEATTLE_GRAY_CITIES = ['DOWNTOWN', 'AIRPORT'];

/** Airport の留置上限 */
const AIRPORT_CAPACITY = 3;

/**
 * Gray City かどうかを判定
 */
export function isGrayCity(cityId: string): boolean {
  return SEATTLE_GRAY_CITIES.includes(cityId);
}

/**
 * バリデーション結果
 */
export interface MoveGoodsSeattleValidationResult {
  success: boolean;
  error?: string;
  message?: string;
}

/**
 * Seattle 商品輸送のバリデーション
 *
 * @param G - Seattle ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 * @param args - 商品輸送引数
 * @returns バリデーション結果
 */
export function validateMoveGoodsSeattle(
  G: SeattleGameState,
  ctx: Ctx,
  args: MoveGoodsSeattleArgs
): MoveGoodsSeattleValidationResult {
  const { fromCityId, toCityId, goodsColor, grayCityAction } = args;
  const player = G.players['0'];

  // 1. フェーズチェック
  if (ctx.phase !== 'moveGoods') {
    return { success: false, error: 'INVALID_PHASE', message: 'Not in moveGoods phase' };
  }

  // 2. 破産チェック
  if (player.isBankrupt) {
    return { success: false, error: 'PLAYER_BANKRUPT', message: 'Bankrupt player cannot move goods' };
  }

  // 3. 輸送回数チェック（共通カウンタを使用）
  const maxDeliveries = G.maxDeliveriesThisPhase ?? G.cargoTransportLevel;
  if (player.turnsCompletedThisPhase >= maxDeliveries) {
    return {
      success: false,
      error: 'MAX_DELIVERIES_REACHED',
      message: `Already delivered ${player.turnsCompletedThisPhase} times (max: ${maxDeliveries})`
    };
  }

  // 4. 出発地点の存在チェック
  const fromHex = G.map.find(h => h.city?.id === fromCityId || h.town?.id === fromCityId);
  if (!fromHex) {
    return { success: false, error: 'CITY_NOT_FOUND', message: `Source city ${fromCityId} not found` };
  }

  // 5. 目的地の存在チェック
  const toHex = G.map.find(h => h.city?.id === toCityId || h.town?.id === toCityId);
  if (!toHex) {
    return { success: false, error: 'CITY_NOT_FOUND', message: `Destination city ${toCityId} not found` };
  }

  // 6. 商品の存在チェック
  // Gray City も含め、全ての商品は hex.goods に保存される
  const goodsOnHex = fromHex.goods || [];

  if (!goodsOnHex.includes(goodsColor)) {
    return {
      success: false,
      error: 'GOODS_NOT_FOUND',
      message: `${goodsColor} goods not found at ${fromCityId}`
    };
  }

  // 7. 目的地の色チェック（Gray City は例外）
  if (!isGrayCity(toCityId)) {
    const destCity = toHex.city;
    if (destCity && destCity.color && !destCity.color.includes(goodsColor)) {
      return {
        success: false,
        error: 'DESTINATION_COLOR_MISMATCH',
        message: `${toCityId} does not accept ${goodsColor} goods`
      };
    }
  }

  // 8. Gray City 輸送時の grayCityAction チェック（Seattle固有）
  if (isGrayCity(toCityId) && !grayCityAction) {
    return {
      success: false,
      error: 'MISSING_GRAY_CITY_ACTION',
      message: 'Must specify grayCityAction (deliver or keep) when delivering to Gray City'
    };
  }

  // 9. Airport 容量チェック（Seattle固有）
  // Airport に留置できる商品は最大3個まで
  if (toCityId === 'AIRPORT' && grayCityAction === 'keep') {
    const airportHex = G.map.find(h => h.city?.id === 'AIRPORT');
    const currentGoods = airportHex?.goods?.length || 0;
    if (currentGoods >= AIRPORT_CAPACITY) {
      return {
        success: false,
        error: 'AIRPORT_CAPACITY_EXCEEDED',
        message: `Airport can only hold ${AIRPORT_CAPACITY} goods (currently: ${currentGoods})`
      };
    }
  }

  // 10. 経路の存在チェック（リンクが存在するか）
  const hasRoute = G.completedLinks.some(link =>
    (link.startId === fromCityId && link.endId === toCityId) ||
    (link.startId === toCityId && link.endId === fromCityId)
  );

  // 直接リンクがない場合は、複数リンクの経路を探す（簡易実装）
  if (!hasRoute) {
    // 複雑な経路探索は既存のfindAllRoutesを使う想定
    // ここでは簡易的にリンク経由でつながっているかをチェック
    const canReach = checkReachability(G, fromCityId, toCityId);
    if (!canReach) {
      return {
        success: false,
        error: 'NO_ROUTE',
        message: `No route from ${fromCityId} to ${toCityId}`
      };
    }
  }

  return { success: true };
}

/**
 * 2つの都市間で到達可能かをチェック（簡易BFS）
 */
function checkReachability(G: SeattleGameState, fromId: string, toId: string): boolean {
  const visited = new Set<string>();
  const queue: string[] = [fromId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === toId) return true;
    if (visited.has(current)) continue;
    visited.add(current);

    // このノードから到達可能なノードを追加
    for (const link of G.completedLinks) {
      if (link.startId === current && !visited.has(link.endId)) {
        queue.push(link.endId);
      }
      if (link.endId === current && !visited.has(link.startId)) {
        queue.push(link.startId);
      }
    }
  }

  return false;
}
